import isNumber from 'lodash/isNumber';

import {QueryReason} from 'common/report/util/query-tracking/types';
import {ActionType, trackLearningAction} from 'common/util/server-tracking';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {DisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {
  isFirstTimeEventFilter,
  isLookupPropertyFilter,
  PropertyFilterFactory,
} from 'common/widgets/property-filter-menu/models/utils';
import {
  generateFilterTrackingProps,
  transformFilterToPropertyItem,
} from 'common/query-builder/query-block/filter-menu/util';
import {
  Action,
  EntryPart,
  FiltersOperator as EntrySectionFilterOperator,
  QueryFilterEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {FullPropertyType, PropertyType, ResourceType} from 'common/report/constants';
import {getIconForPropertyType} from 'common/report/util/properties';

import {BaseEntryRow} from '../base';
import {EntryRowProps} from '../types';
import {
  getIconForBehavior,
  isEventBasedBehavioralSegmentation,
  isFrequencyBehavior,
  isMultiAttributionBehavior,
} from '../behavior/utils';
import {getPropertyType, populateListPropertyAttrs, populateProfileHistoryOptions, setPropertyType} from '../row-util';
// eslint-disable-next-line import/no-cycle
import {FilterEntryRowEl} from './template';
import {OPERATORS_WITH_UNIT} from './constants';

export class FilterEntryRow extends BaseEntryRow<QueryFilterEntry> {
  filter: PropertyFilter;
  template = FilterEntryRowEl;

  constructor(props: EntryRowProps<QueryFilterEntry>) {
    super(props);
    this.filter = this.createFilter(props.entry.event);

    const listOfObjectsProperty = this.section.params.listProperty;
    if (listOfObjectsProperty) {
      // _parent is non-enumerable so it is dropped when propertyFilter is serialized through attrs (to prevent a
      // JSON.stringify circular reference error)
      this.filter.setParent(new PropertyFilter(listOfObjectsProperty));
    }
  }

  defaultActiveEntryPart() {
    if (this.filter.hasSelectedProperty()) {
      return EntryPart.FilterValue;
    }

    return EntryPart.Value;
  }

  selectorIcon(): string {
    if (this.entry.filterOnly) {
      return ``;
    }

    if (this.filter.resourceType === ResourceType.Cohort) {
      return `community`;
    }

    if (isEventBasedBehavioralSegmentation(this.filter) || isMultiAttributionBehavior(this.filter.behavior)) {
      return getIconForBehavior(this.filter.behavior, true);
    }

    if (!this.filter.hasSelectedProperty()) {
      return `search`;
    }

    if (isFirstTimeEventFilter(this.filter)) {
      return `time`;
    }

    return getIconForPropertyType(this.getPropertyType(), {
      isCustom: Boolean(this.filter?.customProperty),
    });
  }

  /**
   * Generates a new empty QueryFilterEntry
   */
  static getNewEntry(): QueryFilterEntry {
    return {
      type: SelectorTypeDefinitions.Filter,
      filtersOperator: null,
      filters: [],
      event: null,
    };
  }

  createFilter(attrs: Partial<PropertyFilter>) {
    return PropertyFilterFactory.createPropertyFilter(attrs, {
      displayContext: DisplayContext.Default,
    });
  }

  isPlaceholder() {
    if (
      isEventBasedBehavioralSegmentation(this.filter) &&
      (this.entry.event.behavior?.property?.value === null ||
        this.entry.event.behavior?.event?.value === null ||
        this.entry.event.behavior?.aggregationOperator === null)
    ) {
      return true;
    }
    return !this.filter.isValid();
  }

  isIntermediatePlaceholder() {
    // allow placeholder when selecting the first list item filter
    if (this.section.params.listProperty && this.index === 0) {
      return true;
    }

    return this.isPlaceholder() && !!this.entry.event;
  }

  handlePropertyItemSelected(selection: PropertyFilter, queryReason?: QueryReason, trackingProps?: Obj) {
    selection = populateListPropertyAttrs(selection);
    if (this.section.isProfileHistoryEnabled()) {
      selection = populateProfileHistoryOptions(selection, this.section.attr(`hide-events`));
    }

    // persist the selected filter operator and filter value if changing between same property types
    if (selection.resourceType !== ResourceType.Cohort && this.filter.propertyType === selection.propertyType) {
      selection.filterValue = this.filter.filterValue;
      selection.filterOperator = this.filter.filterOperator;

      if (selection.joinPropertyType === PropertyType.List || isLookupPropertyFilter(this.filter)) {
        selection.listQuantifier = this.filter.listQuantifier;
      }
    }

    this.updateEntry(selection, {trackingProps, queryReason});
  }

  getNextEntryPart(entry: PropertyFilter) {
    let entryPart = EntryPart.FilterValue;
    if (isEventBasedBehavioralSegmentation(entry)) {
      if (!entry.behavior.aggregationOperator) {
        entryPart = EntryPart.Aggregator;
      } else if (!entry.behavior.event.value) {
        entryPart = EntryPart.SubValue;
      }
    }
    return entryPart;
  }

  setFilterAttribute(attribute: keyof PropertyFilter, value) {
    this.updateEntry(this.filter.setAttribute(attribute, value));
  }

  onChangeOperator() {
    // focus on filter value when operator changes filter to an invalid state
    if (!this.filter.isValid()) {
      this.section.setActiveEntry({index: this.index, part: EntryPart.FilterValue});
    }
  }

  handleFilterDeleteClick() {
    this.section.helpers.onEventActionClick(Action.Remove, this.index);
  }

  handleRenameClick() {
    this.section.promptRename(this.index);
  }

  getSelectedProperty() {
    if (this.filter.resourceType === ResourceType.Cohort) {
      return null;
    }

    return transformFilterToPropertyItem(this.filter);
  }

  handleEntryFilterOperatorChange(filtersOperator: EntrySectionFilterOperator) {
    const updatedEntry = {
      ...this.entry,
      filtersOperator,
    };
    this.section.updateEntries(Action.Edit, {entry: updatedEntry, index: this.index});
  }

  updateEntry(
    filter: PropertyFilter,
    {trackingProps, queryReason}: Partial<{trackingProps: Obj; queryReason: QueryReason}> = {},
  ) {
    const updatedEntry: QueryFilterEntry = {
      ...this.entry,
      event: filter instanceof PropertyFilter ? filter.getAttributes() : filter,
    };

    const isStagingChanges = this.section.isStagingEntryChanges(this.entry, updatedEntry);
    const hadProperty = this.filter.hasSelectedProperty();
    if (isStagingChanges || !hadProperty || filter.behavior) {
      // when changing a committed filter to an invalid filter, or when the filter property
      // is selected for the first time, set focus to the filter value selection
      this.section.setActiveEntry({index: this.index, part: this.getNextEntryPart(filter)});
    }

    // The filter-menu will pass through queryReason in which case we should use that queryReason.
    queryReason = queryReason || QueryReason.QbFilterUpdate;
    const updateStatus = this.section.updateEntries(Action.Edit, {
      entry: updatedEntry,
      index: this.index,
      trackingProps: trackingProps ?? generateFilterTrackingProps(filter),
      queryReason,
    });

    if (queryReason !== QueryReason.QbAutoTriggered && updateStatus.dispatchedUpdate && !this.entry.suppressTracking) {
      // We don't want to track learning action until the complete filter has been created
      trackLearningAction({
        projectId: this.section.attr(`project-id`),
        reportName: this.section.attr(`track-name`),
        actionType: ActionType.Filter,
        resourceType: filter.resourceType,
      });
    }
  }

  getPropertyType(): FullPropertyType {
    return getPropertyType(this.entry.event);
  }

  setPropertyType(toType: FullPropertyType) {
    // do nothing if selecting the same type
    const currentType = this.getPropertyType();
    if (currentType.type === toType.type && currentType.isList === toType.isList) {
      return;
    }

    const event = setPropertyType(this.entry.event, toType, true);

    this.section.updateEntries(Action.Edit, {
      entry: {
        ...this.entry,
        event,
      },
      index: this.index,
    });
  }

  getUnit() {
    const filterValue = this.entry.event?.filterValue;
    // For lists we just put 2 to represent that it's always plural
    const val = Array.isArray(filterValue) ? 2 : this.entry.event?.filterValue;
    // TODO disable for now while we work on diff designs
    if (
      isNumber(val) &&
      isFrequencyBehavior(this.entry.event?.behavior) &&
      OPERATORS_WITH_UNIT.includes(this.entry.event.filterOperator)
    ) {
      return null;
      // return pluralize(`time`, val);
    }
    return null;
  }
}
